import { useEffect, useRef } from "react";

// - - - - - - - - - -
// Custom right-click context menu (#105 phase 4c).
//
// Renders at a given screen position with a list of actions. Closes on
// outside click, Escape key, or after an action is invoked. No third-party
// dependency per the project's library philosophy.
//
// Items shape: [{ label, onClick, disabled?, title? }, ...]
//
// Callers control visibility via the `position` prop:
//   position = { x: clientX, y: clientY } → menu visible at that point
//   position = null                       → menu hidden

export const ContextMenu = ({ position, items, onClose }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!position) return;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    // Right-click anywhere outside also closes (default browser menu would
    // open elsewhere; we suppress nothing — just close ours).
    const onContextMenu = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("contextmenu", onContextMenu);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, [position, onClose]);

  if (!position) return null;

  // Clamp menu position so it stays within the viewport. Approximate
  // menu dimensions used for the calculation; Phase 5 could measure precisely.
  const menuWidth = 200;
  const menuHeight = items.length * 36 + 8;
  const left = Math.min(position.x, window.innerWidth - menuWidth - 8);
  const top = Math.min(position.y, window.innerHeight - menuHeight - 8);

  return (
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 9999,
        minWidth: `${menuWidth}px`,
        backgroundColor: "rgb(var(--color-primary))",
        color: "rgb(var(--color-text-primary))",
        border: "1px solid rgba(89, 89, 89, 0.4)",
        borderRadius: "4px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
        padding: "4px 0",
      }}
    >
      {items.map((item, index) => (
        <button
          key={index}
          role="menuitem"
          disabled={item.disabled}
          title={item.title}
          onClick={() => {
            if (item.disabled) return;
            item.onClick();
            onClose();
          }}
          className={`w-full text-left text-sm px-3 py-2 ${
            item.disabled
              ? "opacity-40 cursor-not-allowed"
              : "hover:bg-skin-base-hover cursor-pointer"
          }`}
          style={{
            transition: "background-color 100ms",
            color: "rgb(var(--color-text-primary))",
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};

export default ContextMenu;
